import {FC, useEffect, useState} from 'react';
//formik
import {Field, useFormikContext} from 'formik';
import {CreateEventFormValues} from '../../formConfiguration';
//mui
import {Box, InputAdornment, Typography} from '@mui/material';
//constants
import {FIELD, FIELD_LABEL, FORM, PLACEHOLDER, START, SUBTITLE1} from '@/utils/constants';
//icons
import {ReactComponent as LocationIcon} from '@/assets/icons/location.svg';
//helpers
import {setRequiredLabel} from '@/utils/helpers';
//components
import Input from '@/shared/components/Inputs/Input';
import DatePicker from '@/shared/components/DatePicker';
import TimePicker from '@/shared/components/TimePicker';
import Switch from '@/shared/components/Switches/Switch';
//styles
import {formContentStyle} from './FormContent.styles';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
import slugify from 'slugify';
import ColorPicker from '@/shared/components/ColorPicker';
import FormHelperText from '@/shared/components/FormHelperText';
import {currencyFormatter} from '@/utils/helpers/currency';

const FormContent: FC = () => {
  const baseURL = process.env.REACT_APP_FE_URL;

  const [isGoalAmountFocused, setIsGoalAmountFocused] = useState(false);
  const [rawInput, setRawInput] = useState<string | null>(null);

  const {
    values: {name, goalAmount, isGoalAmount, description},
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<CreateEventFormValues>();

  const currentDescriptionLength = description.length;

  useEffect(() => {
    const sluggedUrl = slugify(name, {
      replacement: '-',
      remove: undefined,
      lower: true,
      strict: true,
      trim: true,
    });

    setFieldValue(FIELD.customizeWebpage, sluggedUrl);
    setFieldTouched(FIELD.customizeWebpage, false);
  }, [name]);

  const handleUrlChange = (value: string) => {
    const processedValue = value.trim().toLowerCase().replace(/\s/g, '-').replace(/-{2,}/g, '-');
    setFieldValue('customizeWebpage', processedValue);
  };

  return (
    <Box component={FORM} sx={formContentStyle.wrapper}>
      <Box sx={formContentStyle.form}>
        <Box sx={formContentStyle.content}>
          <Box sx={formContentStyle.detailsWrapper}>
            <Typography variant={SUBTITLE1}>Event Details:</Typography>
            <Box sx={formContentStyle.urlWrapper}>
              <Box sx={formContentStyle.fieldHalf}>
                <Field
                  id={FIELD.name}
                  name={FIELD.name}
                  value={name}
                  placeholder={PLACEHOLDER.eventName}
                  label={setRequiredLabel(FIELD_LABEL.eventName)}
                  component={Input}
                />
              </Box>

              <Box sx={formContentStyle.fieldHalf}>
                <Field
                  id={FIELD.customizeWebpage}
                  name={FIELD.customizeWebpage}
                  label={FIELD_LABEL.customizeWebpage}
                  component={Input}
                  onChange={(value: string) => handleUrlChange(value)}
                  InputProps={{
                    startAdornment: <Box sx={formContentStyle.urlPrefix}>{baseURL}/</Box>,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{position: 'relative'}}>
              <Field
                id={FIELD.description}
                name={FIELD.description}
                value={description}
                rows={6}
                sx={formContentStyle.description}
                placeholder={PLACEHOLDER.eventDescription}
                label={FIELD_LABEL.eventDescription}
                component={Input}
                multiline
                inputProps={{
                  maxLength: 500,
                }}
              />
              <FormHelperText
                title={`${currentDescriptionLength}/500`}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 0.5,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          </Box>
          <Box sx={formContentStyle.amount}>
            <Typography variant={SUBTITLE1} sx={formContentStyle.title}>
              Branding & Goal :
            </Typography>

            <Box sx={formContentStyle.brandingWrapper}>
              <Box sx={formContentStyle.logoWrapper}>
                <Field
                  id={FIELD.logo}
                  name={FIELD.logo}
                  label={setRequiredLabel(FIELD_LABEL.eventLogoOrImage)}
                  component={GreyUploadImage}
                  parentClassName={formContentStyle.logo}
                />
                <Typography sx={formContentStyle.logoHint}>Recommended size: 1200 × 600 px</Typography>
              </Box>

              <Box sx={formContentStyle.colorField}>
                <Field
                  id={FIELD.primaryColor}
                  name={FIELD.primaryColor}
                  label={FIELD_LABEL.primaryColor}
                  defaultValue="#26B5ED"
                  component={ColorPicker}
                  parentClassName={{position: 'relative'}}
                />
              </Box>

              <Box sx={formContentStyle.goalRow}>
                <Box sx={formContentStyle.goalField}>
                  <Field
                    id={FIELD.goalAmount}
                    name={FIELD.goalAmount}
                    value={
                      isGoalAmountFocused
                        ? rawInput ?? (goalAmount !== undefined && goalAmount !== null ? String(goalAmount) : '')
                        : goalAmount !== undefined && goalAmount !== null
                        ? currencyFormatter.format(goalAmount)
                        : ''
                    }
                    sx={formContentStyle.goalAmount}
                    placeholder={PLACEHOLDER.zero}
                    label={FIELD_LABEL.goalAmount}
                    component={Input}
                    type="text"
                    inputMode="decimal"
                    disabled={!isGoalAmount}
                    onFocus={() => {
                      setIsGoalAmountFocused(true);
                      setRawInput(goalAmount !== undefined && goalAmount !== null ? String(goalAmount) : '');
                    }}
                    onBlur={() => {
                      setIsGoalAmountFocused(false);
                      setRawInput(null);
                    }}
                    onChange={(value: string) => {
                      const raw = value.replace(/[^0-9.]/g, '');
                      setRawInput(raw);
                      setFieldValue(FIELD.goalAmount, raw === '' || raw === '.' ? undefined : Number(raw));
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position={START} sx={formContentStyle.goalAmountIcon}>
                          $
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography sx={formContentStyle.goalHint}>Used to track progress on your dashboard.</Typography>
                </Box>

                <Field
                  id={FIELD.isGoalAmount}
                  name={FIELD.isGoalAmount}
                  sx={formContentStyle.goalSwitch}
                  component={Switch}
                  checked={isGoalAmount}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={formContentStyle.sceheduleWrapper}>
          <Typography variant={SUBTITLE1}>Schedule & Location:</Typography>
          <Box sx={formContentStyle.locationWrapper}>
            <Box sx={formContentStyle.dateWrapper}>
              <Box sx={formContentStyle.date}>
                <Field
                  id={FIELD.startDate}
                  name={FIELD.startDate}
                  label={setRequiredLabel(FIELD_LABEL.eventStartDate)}
                  component={DatePicker}
                  sx={formContentStyle.dateMonthYear}
                />
                <Field
                  id={FIELD.startTime}
                  name={FIELD.startTime}
                  label={setRequiredLabel(FIELD_LABEL.startTime)}
                  component={TimePicker}
                  parentClassName={formContentStyle.time}
                />
              </Box>
              <Box sx={formContentStyle.date}>
                <Field
                  id={FIELD.endDate}
                  name={FIELD.endDate}
                  label={setRequiredLabel(FIELD_LABEL.eventEndDate)}
                  component={DatePicker}
                  sx={formContentStyle.dateMonthYear}
                />
                <Field
                  id={FIELD.endTime}
                  name={FIELD.endTime}
                  label={setRequiredLabel(FIELD_LABEL.eventEndDate)}
                  component={TimePicker}
                  parentClassName={formContentStyle.time}
                />
              </Box>
            </Box>

            <Field
              id={FIELD.location}
              name={FIELD.location}
              placeholder={PLACEHOLDER.eventLocation}
              label={setRequiredLabel(FIELD_LABEL.eventLocation)}
              component={Input}
              InputProps={{startAdornment: <LocationIcon />}}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FormContent;
